import {
  HttpEvent,
  HttpHandlerFn,
  HttpHeaders,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { WindowRef } from '@boels/helpers';
import { from, Observable, of, switchMap, tap } from 'rxjs';

export function BoelsBrowserHttpCacheInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  const cacheService = inject(BoelsBrowserHttpCacheService);

  if (req.method !== 'GET' || !shouldCache(req)) {
    return next(req);
  }

  function shouldCache(req: HttpRequest<any>): boolean {
    return req.url.includes('cms/components');
  }

  return from(cacheService.getCachedResponse(req)).pipe(
    switchMap(async (cachedResponse) => {
      if (cachedResponse) {
        return of(
          new HttpResponse({
            body: await cachedResponse.json(),
            status: cachedResponse.status,
            statusText: cachedResponse.statusText,
            headers: new HttpHeaders(cachedResponse.headers),
          }),
        );
      }
      return next(req).pipe(
        tap((event) => {
          if (event instanceof HttpResponse) {
            cacheService
              .cacheResponse(req, event)
              .catch((err) => console.error('Error caching response:', err));
          }
        }),
      );
    }),
    switchMap((event) => event),
  );
}

@Injectable()
export class BoelsBrowserHttpCacheService {
  #winRef = inject(WindowRef);
  #destroyRef = inject(DestroyRef);
  #cacheName = 'boels-components-cache';

  constructor() {
    this.#winRef.nativeWindow?.addEventListener(
      'beforeunload',
      this.#clearCache.bind(this, this.#winRef.nativeWindow),
    );
    this.#destroyRef.onDestroy(() =>
      this.#winRef.nativeWindow?.removeEventListener(
        'beforeunload',
        this.#clearCache.bind(this, this.#winRef.nativeWindow),
      ),
    );
  }

  async getCachedResponse(req: HttpRequest<any>): Promise<Response | null> {
    const cache = await caches.open(this.#cacheName);
    const cachedResponse = await cache.match(this.#getCacheKey(req));
    return cachedResponse ? cachedResponse : null;
  }

  async cacheResponse(
    req: HttpRequest<any>,
    response: HttpResponse<any>,
  ): Promise<void> {
    const cache = await caches.open(this.#cacheName);
    const responseToCache = new Response(JSON.stringify(response.body), {
      status: response.status,
      statusText: response.statusText,
      headers: this.#httpHeadersToObjectWithMultipleValues(response.headers),
    });
    await cache.put(this.#getCacheKey(req), responseToCache);
  }

  async #clearCache(nativeWindow: Window | undefined): Promise<void> {
    try {
      if (nativeWindow && 'caches' in nativeWindow) {
        if (await caches.has(this.#cacheName)) {
          await caches.delete(this.#cacheName);
        }
      }
    } catch (error) {
      console.error('Error clearing CacheStorage:', error);
    }
  }

  #httpHeadersToObjectWithMultipleValues(
    headers: HttpHeaders,
  ): Record<string, string> {
    const result: Record<string, string> = {};
    headers.keys().forEach((key) => {
      const values = headers.getAll(key);
      if (values) {
        result[key] = values.join(', ');
      }
    });
    return result;
  }

  #getCacheKey(req: HttpRequest<any>): string {
    return req.urlWithParams;
  }
}
